import React, { useEffect, useRef, useState } from 'react';

interface PricePoint {
  timestamp: Date;
  price: number;
  volume: number;
}

interface MarketPriceChartProps {
  data: PricePoint[];
  width: number;
  height: number;
}

export const MarketPriceChart: React.FC<MarketPriceChartProps> = ({ data, width, height }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, width, height);

    // Calculate bounds with safety checks
    const prices = data.map(d => d.price).filter(p => !isNaN(p) && isFinite(p));
    if (prices.length === 0) return;
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;

    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Draw grid lines
    ctx.strokeStyle = '#21262d';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines (5 lines)
    for (let i = 0; i <= 4; i++) {
      const y = padding + (chartHeight / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      
      // Price labels
      const price = maxPrice - (priceRange / 4) * i;
      ctx.fillStyle = '#6e7681';
      ctx.font = '10px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`₱${Math.floor(price)}`, padding - 5, y + 3);
    }

    // Vertical grid lines (time)
    const timeStep = Math.max(1, Math.floor(data.length / 6));
    for (let i = 0; i < data.length; i += timeStep) {
      const x = padding + (chartWidth / (data.length - 1)) * i;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }

    // Draw data points as squares
    const divisor = Math.max(1, data.length - 1);
    const squareSize = 6;
    
    data.forEach((point, i) => {
      if (!isNaN(point.price) && isFinite(point.price)) {
        const x = padding + (chartWidth / divisor) * i;
        const y = padding + chartHeight - ((point.price - minPrice) / priceRange) * chartHeight;
        
        // Draw square
        if (i === hoveredIndex) {
          ctx.fillStyle = '#fbbf24';
          ctx.fillRect(x - squareSize / 2, y - squareSize / 2, squareSize, squareSize);
        } else {
          ctx.fillStyle = '#58a6ff';
          ctx.fillRect(x - squareSize / 2, y - squareSize / 2, squareSize, squareSize);
        }
      }
    });

  }, [data, width, height, hoveredIndex]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    const prices = data.map(d => d.price).filter(p => !isNaN(p) && isFinite(p));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;

    const divisor = Math.max(1, data.length - 1);
    const squareSize = 6;
    let foundIndex: number | null = null;

    // Check if mouse is over any data point
    for (let i = 0; i < data.length; i++) {
      const x = padding + (chartWidth / divisor) * i;
      const y = padding + chartHeight - ((data[i].price - minPrice) / priceRange) * chartHeight;

      const distance = Math.sqrt((mouseX - x) ** 2 + (mouseY - y) ** 2);
      if (distance < squareSize + 4) {
        foundIndex = i;
        setTooltipPos({ x: mouseX, y: mouseY });
        break;
      }
    }

    setHoveredIndex(foundIndex);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltipPos(null);
  };

  if (data.length === 0) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0d1117',
        border: '1px solid #21262d',
        color: '#6e7681',
        fontFamily: 'monospace',
        fontSize: 12,
      }}>
        No price history available
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          border: '1px solid #21262d',
          background: '#0d1117',
          cursor: hoveredIndex !== null ? 'pointer' : 'default',
        }}
      />
      {hoveredIndex !== null && tooltipPos && (
        <div
          style={{
            position: 'absolute',
            left: tooltipPos.x + 10,
            top: tooltipPos.y - 30,
            background: '#1f2937',
            border: '1px solid #30363d',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '11px',
            fontFamily: 'monospace',
            color: '#e6edf3',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <div>₱{Math.floor(data[hoveredIndex].price)}</div>
          <div style={{ color: '#8b949e', fontSize: '10px' }}>
            Vol: {data[hoveredIndex].volume}
          </div>
          <div style={{ color: '#8b949e', fontSize: '10px' }}>
            {data[hoveredIndex].timestamp.toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
};




